import type { SEOIntent, SEOOfferType } from "@/lib/seo-intents";

export type PurchaseIntentBand = "low" | "medium" | "high";

export interface PurchaseIntentScoreInput {
  intent: SEOIntent;
  sourcePath?: string;
  primaryOffer?: SEOOfferType;
}

export interface PurchaseIntentScore {
  score: number;
  band: PurchaseIntentBand;
  reason: string;
}

const INTENT_BASE_SCORE: Record<SEOIntent, number> = {
  remedy_dosha: 88,
  chart_report: 78,
  timing_muhurat: 74,
  festival_ritual: 68,
  daily_guidance: 54,
  generic: 42,
};

const HIGH_INTENT_PATH_HINTS = [
  "kundli-matching",
  "compatibility",
  "mangal-dosha",
  "kaal-sarp",
  "pitra-dosha",
  "sade-sati",
  "muhurat",
  "rahu-kaal",
  "panchang",
];

const LOW_INTENT_PATH_HINTS = ["rashifal", "daily-horoscope", "today-horoscope"];

function clampScore(score: number) {
  return Math.max(0, Math.min(100, Math.round(score)));
}

// Offer-shown threshold: sessions at or above this score are "high intent"
// and surface a revenue offer. Lowered from 75 → 55 (2026-05-18) after
// funnel analysis found 98.2% of agent decisions never showed an offer —
// avg score was 73.59 but only score ≥75 qualified. Score <55 is logged
// as "low" via purchase_intent_band in PostHog for future ratcheting.
// See: docs/reference/historical-ishvaram/conversion-funnel-analysis-2026-05-18.md Fix #1.
const OFFER_MIN_SCORE = 55;

function bandForScore(score: number): PurchaseIntentBand {
  if (score >= OFFER_MIN_SCORE) return "high";
  return "low";
}

export function scorePurchaseIntent({
  intent,
  sourcePath = "",
  primaryOffer,
}: PurchaseIntentScoreInput): PurchaseIntentScore {
  const normalizedPath = sourcePath.toLowerCase();
  const reasons: string[] = [`intent:${intent}`];
  let score = INTENT_BASE_SCORE[intent] ?? INTENT_BASE_SCORE.generic;

  if (primaryOffer === "puja" || primaryOffer === "product") {
    score += 5;
    reasons.push(`offer:${primaryOffer}`);
  }

  if (HIGH_INTENT_PATH_HINTS.some((hint) => normalizedPath.includes(hint))) {
    score += 8;
    reasons.push("high_intent_path");
  }

  if (LOW_INTENT_PATH_HINTS.some((hint) => normalizedPath.includes(hint))) {
    score -= 8;
    reasons.push("low_intent_path");
  }

  const finalScore = clampScore(score);
  return {
    score: finalScore,
    band: bandForScore(finalScore),
    reason: reasons.join("+"),
  };
}
